import { Button } from "@/components/ui/button";
import {
  createMerchantOverviewAggregates,
  createOverviewRows,
  createOverviewSummary,
} from "@/components/compliance/admin-compliance-overview.utils";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { canAdminRead } from "@/lib/auth";
import {
  getAdminCasesQueryOptions,
  getAdminMerchantsQueryOptions,
  getAdminOverviewQueryOptions,
  getAdminWorkflowStatsQueryOptions,
} from "@/lib/compliance-query-keys";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
  type Column,
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowDown, ArrowUp, ChevronRight, ChevronsUpDown } from "lucide-react";
import { useMemo, useState } from "react";
import type {
  OverviewWorkflowStatus,
  RequirementStatus,
} from "@/types/compliance";

const percentFormatter = new Intl.NumberFormat("en-GB", {
  style: "percent",
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

function formatPercent(value: number) {
  return percentFormatter.format(Math.max(0, Math.min(1, value)));
}

function progressWidth(value: number) {
  return `${Math.max(0, Math.min(100, value * 100))}%`;
}

interface MerchantOverviewRow {
  merchantId: string | null;
  merchantName: string;
  applications: number;
  submitted: number;
  required: number;
  rejected: number;
  waitingOnMerchant: number;
  waitingOnUs: number;
  submittedWithoutEvidence: number;
  totalTracked: number;
  completionRate: number;
  reviewOpenCases: number;
}

const WORKFLOW_STATUS_ORDER: OverviewWorkflowStatus[] = [
  "WAITING_FOR_INPUT",
  "PARTIAL",
  "PENDING_VOLUME_REVIEW",
  "APPROVED",
  "SENT_TO_OPTIMUS",
];

const REQUIREMENT_STATUS_ORDER: RequirementStatus[] = [
  "REQUIRED",
  "SUBMITTED",
  "APPROVED",
  "REJECTED",
];

const WORKFLOW_STATUS_LABEL: Record<OverviewWorkflowStatus, string> = {
  WAITING_FOR_INPUT: "Waiting For Input",
  PARTIAL: "Partially Completed",
  PENDING_VOLUME_REVIEW: "Pending Volume Review",
  APPROVED: "Approved",
  SENT_TO_OPTIMUS: "Sent To Optimus",
};

const REQUIREMENT_STATUS_LABEL: Record<RequirementStatus, string> = {
  REQUIRED: "Required",
  SUBMITTED: "Submitted",
  APPROVED: "Approved",
  REJECTED: "Rejected",
};

export const Route = createFileRoute(
  "/(app)/_admin-layout/admin_/compliance/overview",
)({
  loader: async ({ context: { queryClient, accessToken, authorities } }) => {
    if (!canAdminRead(authorities)) return;
    await Promise.all([
      queryClient.ensureQueryData(
        getAdminOverviewQueryOptions({ accessToken }),
      ),
      queryClient.ensureQueryData(
        getAdminMerchantsQueryOptions({ accessToken }),
      ),
      queryClient.ensureQueryData(
        getAdminCasesQueryOptions({
          accessToken,
          limit: 100,
          workflowStatuses: "PENDING_VOLUME_REVIEW",
        }),
      ),
      queryClient.ensureQueryData(
        getAdminWorkflowStatsQueryOptions({ accessToken }),
      ),
    ]);
  },
  component: AdminComplianceOverview,
});

function AdminComplianceOverview() {
  const context = Route.useRouteContext();
  const [merchantFilter, setMerchantFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>([
    { id: "waitingOnMerchant", desc: true },
  ]);

  const overviewQuery = useSuspenseQuery(
    getAdminOverviewQueryOptions({ accessToken: context.accessToken }),
  );

  const merchantsQuery = useSuspenseQuery(
    getAdminMerchantsQueryOptions({ accessToken: context.accessToken }),
  );
  const pendingReviewCasesQuery = useSuspenseQuery(
    getAdminCasesQueryOptions({
      accessToken: context.accessToken,
      limit: 100,
      workflowStatuses: "PENDING_VOLUME_REVIEW",
    }),
  );
  const workflowStatsQuery = useSuspenseQuery(
    getAdminWorkflowStatsQueryOptions({ accessToken: context.accessToken }),
  );

  const merchantNameById = useMemo(
    () =>
      Object.fromEntries(
        merchantsQuery.data.map((merchant) => [
          merchant.merchantId,
          merchant.companyName,
        ]),
      ),
    [merchantsQuery.data],
  );

  const items = overviewQuery.data;
  const rows = useMemo(() => createOverviewRows(items), [items]);

  const summary = useMemo(() => createOverviewSummary(rows), [rows]);

  const pendingReviewTasks = useMemo(
    () =>
      pendingReviewCasesQuery.data.items
        .map((item) => ({
          merchantId: item.merchantId,
          submittedCount: item.requirements.filter(
            (requirement) => requirement.status === "SUBMITTED",
          ).length,
        }))
        .filter((item) => item.submittedCount > 0),
    [pendingReviewCasesQuery.data.items],
  );

  const pendingReviewSummary = useMemo(() => {
    const merchantsAwaiting = new Set<string>();
    let totalSubmitted = 0;
    for (const item of pendingReviewTasks) {
      totalSubmitted += item.submittedCount;
      if (item.merchantId) merchantsAwaiting.add(item.merchantId);
    }
    return {
      totalSubmitted,
      openCases: pendingReviewTasks.length,
      merchantsAwaiting: merchantsAwaiting.size,
    };
  }, [pendingReviewTasks]);

  const workflowStats = workflowStatsQuery.data;

  const pendingReviewByMerchant = useMemo(() => {
    const byMerchant = new Map<string, { openCases: number }>();
    for (const task of pendingReviewTasks) {
      const key = task.merchantId ?? "unknown";
      const current = byMerchant.get(key);
      if (!current) {
        byMerchant.set(key, { openCases: 1 });
        continue;
      }
      current.openCases += 1;
    }
    return byMerchant;
  }, [pendingReviewTasks]);

  const merchantRows = useMemo<MerchantOverviewRow[]>(() => {
    const merchantAggregates = createMerchantOverviewAggregates(rows);
    const merchantsById = new Map<string, MerchantOverviewRow>();

    for (const [key, item] of merchantAggregates) {
      const merchantName = item.merchantId
        ? (merchantNameById[item.merchantId] ?? `Merchant ${item.merchantId}`)
        : "Unknown merchant";

      merchantsById.set(key, {
        merchantId: item.merchantId,
        merchantName,
        applications: item.applications,
        submitted: item.submitted,
        required: item.required,
        rejected: item.rejected,
        waitingOnMerchant: item.waitingOnMerchant,
        waitingOnUs: item.waitingOnUs,
        submittedWithoutEvidence: item.submittedWithoutEvidence,
        totalTracked: item.totalTracked,
        completionRate: item.completionRate,
        reviewOpenCases: pendingReviewByMerchant.get(key)?.openCases ?? 0,
      });
    }

    return Array.from(merchantsById.values());
  }, [merchantNameById, pendingReviewByMerchant, rows]);

  const columns = useMemo<ColumnDef<MerchantOverviewRow>[]>(
    () => [
      {
        accessorKey: "merchantName",
        header: ({ column }) => (
          <SortableHeader column={column} label="Merchant" align="left" />
        ),
        cell: ({ row }) => {
          const merchant = row.original;
          return (
            <div>
              <p className="font-medium text-slate-900">
                {merchant.merchantName}
              </p>
              <p className="text-xs text-slate-500">
                {merchant.applications} application
                {merchant.applications === 1 ? "" : "s"}
              </p>
            </div>
          );
        },
      },
      {
        accessorKey: "waitingOnMerchant",
        header: ({ column }) => (
          <SortableHeader
            column={column}
            label="Waiting On Merchant"
            align="right"
          />
        ),
        cell: ({ row }) => (
          <p className="text-right text-sm font-medium text-amber-700">
            {row.original.waitingOnMerchant}
          </p>
        ),
      },
      {
        accessorKey: "waitingOnUs",
        header: ({ column }) => (
          <SortableHeader column={column} label="Waiting On Us" align="right" />
        ),
        cell: ({ row }) => (
          <p className="text-right text-sm font-medium text-red-700">
            {row.original.waitingOnUs}
          </p>
        ),
      },
      {
        accessorKey: "reviewOpenCases",
        header: ({ column }) => (
          <SortableHeader
            column={column}
            label="Open Review Cases"
            align="right"
          />
        ),
        cell: ({ row }) => (
          <p className="text-right text-sm text-slate-700">
            {row.original.reviewOpenCases}
          </p>
        ),
      },
      {
        accessorKey: "completionRate",
        header: ({ column }) => (
          <SortableHeader column={column} label="Completion" align="right" />
        ),
        cell: ({ row }) => (
          <div className="ml-auto w-24 text-right">
            <p className="text-sm font-semibold text-volume-text-purple">
              {formatPercent(row.original.completionRate)}
            </p>
            <div className="ml-auto mt-1 h-1.5 w-24 rounded-full bg-slate-100">
              <div
                className="h-1.5 rounded-full bg-volume-text-purple"
                style={{ width: progressWidth(row.original.completionRate) }}
              />
            </div>
          </div>
        ),
      },
      {
        accessorKey: "totalTracked",
        header: ({ column }) => (
          <SortableHeader column={column} label="Total Tracked" align="right" />
        ),
        cell: ({ row }) => (
          <p className="text-right text-sm text-slate-700">
            {row.original.totalTracked}
          </p>
        ),
      },
      {
        id: "action",
        enableSorting: false,
        header: () => <div className="text-right">Action</div>,
        cell: ({ row }) => (
          <div className="text-right">
            {row.original.merchantId ? (
              <Link
                to="/admin/compliance/cases"
                search={{ merchantId: row.original.merchantId }}
                preload="intent"
              >
                <Button variant="ghost" size="sm">
                  Open queue
                </Button>
              </Link>
            ) : (
              <Link to="/admin/compliance/cases" preload="intent">
                <Button variant="ghost" size="sm">
                  Open queue
                </Button>
              </Link>
            )}
          </div>
        ),
      },
    ],
    [],
  );

  // TanStack Table returns non-memoizable functions; no compiler-compatible API exists.
  // eslint-disable-next-line react-hooks-js/incompatible-library
  const table = useReactTable({
    data: merchantRows,
    columns,
    state: {
      sorting,
      globalFilter: merchantFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setMerchantFilter,
    globalFilterFn: (row, _columnId, filterValue) => {
      const needle = String(filterValue).trim().toLowerCase();
      if (!needle) return true;
      const merchantName = row.original.merchantName.toLowerCase();
      const merchantId = row.original.merchantId?.toLowerCase() ?? "";
      return merchantName.includes(needle) || merchantId.includes(needle);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  if (items.length === 0) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 py-8 text-center text-sm text-slate-600">
        No compliance data available yet.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-lg shadow-slate-200/60 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-[0.16em] text-volume-text-purple/70">
              Operational Snapshot
            </h2>
            <p className="mt-1 text-lg font-semibold text-volume-text-purple sm:text-xl">
              At-a-glance view of completion, queue pressure, and ownership.
            </p>
          </div>
          <Link to="/admin/compliance/cases" preload="intent">
            <Button variant="outline" size="sm">
              Open cases
            </Button>
          </Link>
        </div>
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <QueueTile
            label="Merchant Completion"
            value={formatPercent(summary.merchantCompletionRate)}
            subtext={`${summary.totalTracked - summary.waitingOnMerchant} of ${summary.totalTracked} actions no longer waiting on merchant`}
            className="border-emerald-300 bg-emerald-50 text-emerald-800"
          />
          <QueueTile
            label="Actions Waiting On Us"
            value={summary.waitingOnUs}
            subtext={
              summary.submittedWithoutEvidence > 0
                ? `${pendingReviewSummary.merchantsAwaiting} merchants currently waiting on review, including ${summary.submittedWithoutEvidence} submitted without evidence`
                : `${pendingReviewSummary.merchantsAwaiting} merchants currently waiting on review`
            }
            className="border-red-300 bg-red-50 text-red-800"
          />
          <QueueTile
            label="Actions Waiting On Merchant"
            value={summary.waitingOnMerchant}
            subtext={`${summary.impactedApplications} applications still blocked`}
            className="border-amber-300 bg-amber-50 text-amber-800"
          />
          <QueueTile
            label="Open Review Cases"
            value={pendingReviewSummary.openCases}
            subtext={`${pendingReviewSummary.totalSubmitted} submitted actions currently in review queue`}
            className="border-slate-300 bg-slate-50 text-slate-700"
          />
        </div>
        <div className="mt-4">
          <CombinedStatusBoard
            workflowItems={WORKFLOW_STATUS_ORDER.map((status) => ({
              key: status,
              label: WORKFLOW_STATUS_LABEL[status],
              value: workflowStats.workflowCounts[status],
            }))}
            requirementItems={REQUIREMENT_STATUS_ORDER.map((status) => ({
              key: status,
              label: REQUIREMENT_STATUS_LABEL[status],
              value: workflowStats.requirementCounts[status],
            }))}
          />
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-lg shadow-slate-200/60 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-volume-text-purple/70">
              Merchant Queue Overview
            </h3>
            <p className="mt-1 text-sm font-medium text-slate-800">
              Single-table view for merchant triage with sortable risk and queue
              signals.
            </p>
          </div>
          <Link to="/admin/compliance/cases" preload="intent">
            <Button variant="outline" size="sm">
              Open all cases
            </Button>
          </Link>
        </div>

        <div className="mt-3">
          <Input
            value={merchantFilter}
            onChange={(event) => setMerchantFilter(event.target.value)}
            placeholder="Filter by merchant name or ID"
            className="h-9 w-full max-w-sm"
          />
        </div>

        <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200">
          <Table>
            <TableHeader className="bg-slate-50/70">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="border-slate-200">
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className="h-9 px-3 py-2 text-xs"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="px-3 py-6 text-center text-sm text-slate-500"
                  >
                    No merchants match this filter.
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} className="border-slate-200">
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id} className="px-3 py-2">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-600">
          <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1">
            {table.getRowModel().rows.length} of {merchantRows.length} merchants
            shown
          </span>
          <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-1">
            Sort any column for quick triage
          </span>
        </div>
      </section>
    </div>
  );
}

function SortableHeader({
  column,
  label,
  align,
}: {
  column: Column<MerchantOverviewRow, unknown>;
  label: string;
  align: "left" | "right";
}) {
  const sorted = column.getIsSorted();

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => column.toggleSorting(sorted === "asc")}
      className={`h-8 px-2 text-xs font-medium text-slate-600 ${
        align === "right" ? "ml-auto flex" : "flex"
      }`}
    >
      {label}
      {sorted === "asc" ? (
        <ArrowUp className="ml-1 h-3.5 w-3.5" />
      ) : sorted === "desc" ? (
        <ArrowDown className="ml-1 h-3.5 w-3.5" />
      ) : (
        <ChevronsUpDown className="ml-1 h-3.5 w-3.5" />
      )}
    </Button>
  );
}

function QueueTile({
  label,
  value,
  subtext,
  className,
}: {
  label: string;
  value: number | string;
  subtext?: string;
  className: string;
}) {
  return (
    <article className={`rounded-xl border p-3 ${className}`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.12em]">
        {label}
      </p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
      {subtext ? <p className="mt-1 text-xs opacity-80">{subtext}</p> : null}
    </article>
  );
}

function StatusTimelineRow({
  label,
  items,
  tone,
}: {
  label: string;
  items: { key: string; label: string; value: number }[];
  tone: "workflow" | "requirement";
}) {
  const arrowTone =
    tone === "workflow" ? "text-volume-text-purple/55" : "text-amber-500/75";
  const valueTone =
    tone === "workflow" ? "text-volume-text-purple" : "text-amber-700";
  const total = items.reduce((acc, item) => acc + item.value, 0);

  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-600">
        {label}
      </p>
      <div className="mt-2 overflow-x-auto pb-1 md:overflow-visible">
        <div className="flex min-w-max items-center gap-1 md:min-w-0 md:w-full">
          {items.map((item, index) => {
            const percentage = total === 0 ? 0 : (item.value / total) * 100;
            return (
              <div key={item.key} className="flex items-center gap-1 md:flex-1">
                <article className="w-28 rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-center md:w-full">
                  <p className="truncate text-[9px] font-medium uppercase tracking-[0.08em] text-slate-600">
                    {item.label}
                  </p>
                  <p
                    className={`mt-1 text-base font-bold leading-none ${valueTone}`}
                  >
                    {item.value}
                  </p>
                  <p className="mt-1 text-[10px] text-slate-500">
                    {percentage.toFixed(0)}%
                  </p>
                </article>
                {index < items.length - 1 ? (
                  <ChevronRight className={`size-4 shrink-0 ${arrowTone}`} />
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CombinedStatusBoard({
  workflowItems,
  requirementItems,
}: {
  workflowItems: { key: string; label: string; value: number }[];
  requirementItems: { key: string; label: string; value: number }[];
}) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-3">
      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-600">
        Case Completion And Requirement Review State
      </p>
      <p className="mt-1 text-xs text-slate-500">
        Top row shows how many cases are in each completion stage. Bottom row
        shows requirement/upload states across those same cases.
      </p>
      <div className="mt-3 space-y-3">
        <StatusTimelineRow
          label="Case Completion Status (Per Case)"
          items={workflowItems}
          tone="workflow"
        />
        <StatusTimelineRow
          label="Requirement And Upload Status (Across Case Requirements)"
          items={requirementItems}
          tone="requirement"
        />
      </div>
    </section>
  );
}
